import React from 'react';
import LegalPageLayout from '../../components/Legal/LegalPageLayout';

const sections = [
  { id: 'acceptance', label: 'Acceptance of Terms' },
  { id: 'service', label: 'Service Description' },
  { id: 'account', label: 'Account & Registration' },
  { id: 'subscription', label: 'Subscription & Billing' },
  { id: 'trial', label: 'Free Trial' },
  { id: 'acceptable-use', label: 'Acceptable Use' },
  { id: 'ip', label: 'Intellectual Property' },
  { id: 'data', label: 'Data Ownership' },
  { id: 'liability', label: 'Limitation of Liability' },
  { id: 'termination', label: 'Termination' },
  { id: 'governing-law', label: 'Governing Law' },
  { id: 'amendments', label: 'Amendments' },
];

const TermsAndConditions = () => (
  <LegalPageLayout
    title="Terms & Conditions"
    subtitle="The rules and agreements that govern your use of CRM Automax."
    lastUpdated="May 7, 2025"
    sections={sections}
  >
    <div className="legal-section" id="acceptance" data-section>
      <h2>1. Acceptance of Terms</h2>
      <p>
        Welcome to CRM Automax. These Terms and Conditions ("Terms") constitute a legally binding
        agreement between you ("User" or "Customer") and CRM Automax ("Company," "we," or "us")
        governing your access to and use of the CRM Automax platform (the "Service").
      </p>
      <p>
        By registering for an account, starting a free trial, or using any part of the Service, you
        acknowledge that you have read, understood, and agree to be bound by these Terms.
      </p>
      <div className="legal-highlight">
        <p>
          If you are entering into these Terms on behalf of a company or legal entity, you represent
          that you have the authority to bind such entity to these Terms.
        </p>
      </div>
    </div>

    <div className="legal-section" id="service" data-section>
      <h2>2. Service Description</h2>
      <p>
        CRM Automax is a cloud-based SaaS Customer Relationship Management platform that enables
        businesses to manage leads, track sales pipelines, coordinate teams, and generate reports.
      </p>
      <ul>
        <li>Lead management and tracking</li>
        <li>Multi-role team management (Super Admin, Manager, Executive, Telecaller, Site Visit Agent)</li>
        <li>Real-time dashboards and reporting</li>
        <li>Notification and alert systems</li>
        <li>Subscription and billing management</li>
      </ul>
      <p>
        We reserve the right to modify, enhance, or discontinue any feature of the Service, with
        material changes communicated to active subscribers in advance.
      </p>
    </div>

    <div className="legal-section" id="account" data-section>
      <h2>3. Account Registration & Security</h2>
      <h3>3.1 Registration</h3>
      <p>
        To access the Service, you must register by providing accurate and complete information
        including your name, company name, email address, and phone number.
      </p>
      <h3>3.2 Account Security</h3>
      <p>You are responsible for maintaining the confidentiality of your credentials. You agree to:</p>
      <ul>
        <li>Use a strong, unique password for your account</li>
        <li>Never share your credentials with any third party</li>
        <li>Immediately notify us of any unauthorized access</li>
      </ul>
      <h3>3.3 One Account Per Company</h3>
      <p>
        Each company may register one Super Admin account. Additional team members are added as
        sub-users, subject to the seat limits of your subscription plan.
      </p>
    </div>

    <div className="legal-section" id="subscription" data-section>
      <h2>4. Subscription, Billing & Payment</h2>
      <h3>4.1 Subscription Plans</h3>
      <p>
        CRM Automax offers various subscription plans with different features, user seat limits, and
        pricing as displayed on our <a href="/pricing">Pricing page</a>.
      </p>
      <h3>4.2 Payment Processing</h3>
      <p>
        All payments are processed securely through <strong>Razorpay Financial Solutions Pvt. Ltd.</strong>,
        an RBI-authorised Payment Aggregator. By making a payment, you also agree to Razorpay's
        <a href="https://razorpay.com/terms/" target="_blank" rel="noreferrer"> Terms of Service</a>.
      </p>
      <h3>4.3 Billing Cycle & Auto-Renewal</h3>
      <p>
        Subscriptions are billed in advance for the selected plan duration. They auto-renew at the
        end of each billing period unless cancelled before the renewal date. You will receive a
        reminder email 7 days before renewal.
      </p>
      <h3>4.4 Taxes</h3>
      <p>
        Applicable GST will be added at checkout. You are responsible for all taxes applicable to
        your purchase under Indian tax law. For refund details, see our <a href="/payment-policy">Payment Policy</a>.
      </p>
    </div>

    <div className="legal-section" id="trial" data-section>
      <h2>5. Free Trial</h2>
      <p>CRM Automax offers a free trial period. During the trial:</p>
      <ul>
        <li>No credit card or payment information is required</li>
        <li>You have access to all features within the trial seat limit</li>
        <li>Data entered during the trial is retained if you upgrade to a paid plan</li>
        <li>The trial automatically ends at expiry — access is suspended but data is retained for 30 days</li>
        <li>Abuse of the trial system may result in immediate account termination</li>
      </ul>
    </div>

    <div className="legal-section" id="acceptable-use" data-section>
      <h2>6. Acceptable Use Policy</h2>
      <p>You agree NOT to use the Service to:</p>
      <ul>
        <li>Violate any applicable Indian or international laws and regulations</li>
        <li>Upload malicious code, viruses, or harmful software</li>
        <li>Attempt unauthorized access to our systems or other users' accounts</li>
        <li>Reverse-engineer or decompile any part of the platform</li>
        <li>Use automated scripts or bots to scrape data from the platform</li>
        <li>Send unsolicited bulk communications (spam) using our notification features</li>
        <li>Infringe upon any third party's intellectual property rights</li>
      </ul>
      <p>
        Violations may result in immediate account suspension or termination without refund,
        and may be reported to appropriate legal authorities.
      </p>
    </div>

    <div className="legal-section" id="ip" data-section>
      <h2>7. Intellectual Property</h2>
      <h3>7.1 Our Property</h3>
      <p>
        CRM Automax, its logo, design, codebase, and all related content are the exclusive
        intellectual property of the Company. No part may be copied or distributed without
        express written consent.
      </p>
      <h3>7.2 Your Content</h3>
      <p>
        You retain full ownership of all data you upload into the CRM. By using the Service,
        you grant us a limited, non-exclusive license to process your data solely to provide
        the Service to you.
      </p>
    </div>

    <div className="legal-section" id="data" data-section>
      <h2>8. Data Ownership & Processing</h2>
      <p>
        You own all data you input into CRM Automax. We act as a Data Processor on your behalf
        in accordance with our <a href="/privacy-policy">Privacy Policy</a>. Upon account
        termination, your data is retained for 30 days for export, then permanently deleted.
      </p>
    </div>

    <div className="legal-section" id="liability" data-section>
      <h2>9. Limitation of Liability</h2>
      <p>
        Our total liability for any claim shall not exceed the total amount paid by you in the
        3 months immediately preceding the event. We are not liable for indirect, consequential,
        or punitive damages, or losses due to third-party service failures.
      </p>
    </div>

    <div className="legal-section" id="termination" data-section>
      <h2>10. Termination</h2>
      <h3>10.1 By You</h3>
      <p>
        You may terminate your account at any time by contacting support. This stops future billing
        but does not entitle you to a refund for the current billing period.
      </p>
      <h3>10.2 By Us</h3>
      <p>We may suspend or terminate your account immediately if you breach these Terms, payment fails,
        or your use poses a security risk. Upon termination, data is retained for 30 days for export.
      </p>
    </div>

    <div className="legal-section" id="governing-law" data-section>
      <h2>11. Governing Law & Dispute Resolution</h2>
      <p>
        These Terms are governed by the laws of India. Disputes shall first be attempted to be
        resolved amicably within 30 days. Thereafter, they shall be subject to the exclusive
        jurisdiction of competent courts in India.
      </p>
    </div>

    <div className="legal-section" id="amendments" data-section>
      <h2>12. Amendments</h2>
      <p>
        We may update these Terms at any time. Material changes will be communicated via email at
        least 14 days before taking effect and displayed within the platform. Continued use of the
        Service constitutes acceptance of updated Terms.
      </p>
      <p>Questions about these Terms? Contact us at <a href="mailto:info@westernflex.com">info@westernflex.com</a></p>
    </div>
  </LegalPageLayout>
);

export default TermsAndConditions;
